import {
  useCompanyVisibilityState,
  useDisplayCompanyName,
  useRecordLastUpdate,
  useSearchTableFromRoute,
} from '@aibees/elephant/hooks';
import { ROUTES } from '@aibees/elephant/router';
import { SearchTable } from '@aibees/elephant/types';
import { Icon, TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { CompanyInfoBadge } from '../company-info-badge/company-info-badge';
import { CompanyProfileDetailPopover } from '../company-profile-detail-popover';
import { QuickView } from '../quick-view';
import { SearchSocialMedia } from '../search-social-media';
import { SocialLink } from '../search-social-media-item';
import * as Styled from './styles';
import * as SX from './sx';
import type { CompanyProfileProps as Props } from './types';

const AvatarFallbackIcon = memo(() => (
  <Icon size={20} name="business" color="grey.400" />
));
AvatarFallbackIcon.displayName = 'CompanyProfile.AvatarFallbackIcon';

const LastUpdateLabel = memo(() => {
  const { lastUpdateCompany } = useRecordLastUpdate();
  return (
    <Typography
      className="last-records-update"
      variant="body5"
      fontWeight={400}
      color="grey.500"
      sx={SX.lastUpdate}
    >
      Last Update: {lastUpdateCompany}
    </Typography>
  );
});
LastUpdateLabel.displayName = 'CompanyProfile.LastUpdateLabel';

export const CompanyProfile = memo(
  ({
    className,
    imageSrc,
    name,
    socialLink,
    id,
    showPopover,
    avatarSize,
    nameProps,
    personData,
    badgeSize,
    hideBadge,
    useCompanyNameDirectly,
  }: Props) => {
    const table = useSearchTableFromRoute();
    const showQuickView = table === SearchTable.Company;
    const link = useMemo(() => ROUTES.company.replace(':id', id ?? '#'), [id]);
    const isCompanySearch = table === SearchTable.Company;
    const companyVisibilityState = useCompanyVisibilityState(
      isCompanySearch ? undefined : personData
    );
    const shouldUseCompanyNameDirectly =
      isCompanySearch || useCompanyNameDirectly;
    const hasFullCompanyData = shouldUseCompanyNameDirectly
      ? true
      : companyVisibilityState.hasFullCompanyData;

    const { variant = 'body2', fontWeight = 600 } = nameProps ?? {};
    const companyDisplayName = useDisplayCompanyName(
      shouldUseCompanyNameDirectly ? undefined : personData,
      name
    );
    const displayName = shouldUseCompanyNameDirectly
      ? name
      : companyDisplayName.displayName;
    const color = shouldUseCompanyNameDirectly
      ? 'grey.700'
      : companyDisplayName.color;

    const avatarContent = (
      <Box>
        <Avatar
          color="white"
          variant="rounded"
          size={avatarSize ?? 32}
          src={imageSrc ?? undefined}
        >
          <AvatarFallbackIcon />
        </Avatar>
      </Box>
    );

    return (
      <Styled.Wrapper
        className={classnames('CompanyProfile', className)}
        sx={SX.root}
      >
        {hideBadge ? (
          avatarContent
        ) : (
          <CompanyInfoBadge
            badgeSize={badgeSize}
            personData={isCompanySearch ? undefined : personData}
          >
            {avatarContent}
          </CompanyInfoBadge>
        )}
        <Box sx={SX.b1}>
          <Box sx={SX.b2}>
            <Box sx={SX.b3}>
              <Box sx={SX.b4}>
                <TextEllipsis
                  value={displayName}
                  w={'100%'}
                  variant={variant}
                  fontWeight={fontWeight}
                  color={color}
                  to={id ? link : undefined}
                />
                {showPopover && (id || !hasFullCompanyData) && (
                  <CompanyProfileDetailPopover
                    companyId={id}
                    disabled={!hasFullCompanyData}
                  />
                )}
              </Box>
              {showQuickView && <QuickView id={id} />}
            </Box>
            <Stack
              direction="row"
              gap={0.5}
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <SearchSocialMedia
                socialLink={socialLink as SocialLink}
                hideLinkOutIcon={false}
              />
              {showQuickView && <LastUpdateLabel />}
            </Stack>
          </Box>
        </Box>
      </Styled.Wrapper>
    );
  }
);

CompanyProfile.displayName = 'CompanyProfile';
